export type FormFieldConfig = {
  name: string
  placeholder?: string
  type: 'text' | 'number' | 'date' | 'email' | 'tel' | 'password' | 'file' | 'select'
  required?: boolean
  options?: Array<{ value: string; label: string }>
}

export type FormContent = {
  title?: string
  introduction_message?: string
  submit_button_text?: string
  form_id?: string
  answer_id?: string
  type?: string
  fields?: FormFieldConfig[]
  answered?: boolean
  submitted?: boolean
  submitted_fields?: Record<string, unknown>
  submitted_fields_redacted?: boolean
}

export type FormHistoryRecord = {
  _id?: string
  role?: string
  type?: string
  content?: unknown
  form_id?: unknown
  formId?: unknown
  answer_id?: unknown
  answerId?: unknown
  submitted?: boolean
  submitted_fields?: unknown
  submittedFields?: unknown
  submitted_fields_redacted?: boolean
  fields?: unknown
  form_config?: unknown
  formConfig?: unknown
  form_data?: unknown
  formData?: unknown
  timestamp?: number
  session_id?: string
}

export type NormalizedFormHistoryMessage = {
  role: 'assistant' | 'user'
  type: 'show_form'
  content: FormContent
  _id?: string
  timestamp?: number
  sessionId?: string
}

export const parseFormObject = (content: unknown): Record<string, any> | null => {
  if (content && typeof content === 'object' && !Array.isArray(content)) return content as Record<string, any>
  if (typeof content !== 'string') return null

  try {
    const parsed = JSON.parse(content)

    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

const hasValidSelectOptions = (field: FormFieldConfig): boolean =>
  field.type !== 'select' ||
  (Array.isArray(field.options) &&
    field.options.length > 0 &&
    field.options.every(
      option =>
        option && typeof option === 'object' && typeof option.value === 'string' && typeof option.label === 'string'
    ))

export const isFormFieldConfigList = (fields: unknown): fields is FormFieldConfig[] =>
  Array.isArray(fields) &&
  fields.length > 0 &&
  fields.every(
    field =>
      field &&
      typeof field === 'object' &&
      typeof (field as FormFieldConfig).name === 'string' &&
      ['text', 'number', 'date', 'email', 'tel', 'password', 'file', 'select'].includes(
        (field as FormFieldConfig).type
      ) &&
      hasValidSelectOptions(field as FormFieldConfig)
  )

export const getFormId = (form: Record<string, any> | null | undefined): string | undefined => {
  const value = form?.form_id ?? form?.formId ?? form?.id ?? form?.config?.form_id ?? form?.form_config?.form_id

  return typeof value === 'string' && value.length > 0 ? value : undefined
}

export const getFormConfig = (
  message: FormHistoryRecord | Record<string, any>,
  contentObject: Record<string, any> | null = parseFormObject(message?.content)
): FormContent | null => {
  const candidate =
    message?.form_config ??
    message?.formConfig ??
    message?.form_data ??
    message?.formData ??
    contentObject?.form_config ??
    contentObject?.formConfig ??
    contentObject?.config ??
    (isFormFieldConfigList(contentObject?.fields) ? contentObject : null) ??
    (isFormFieldConfigList(message?.fields) ? message : null)

  if (!candidate || typeof candidate !== 'object' || !isFormFieldConfigList(candidate.fields)) return null

  const answerId = candidate.answer_id ?? contentObject?.answer_id ?? message?.answer_id

  return {
    title: candidate.title,
    introduction_message: candidate.introduction_message,
    submit_button_text: candidate.submit_button_text,
    form_id: getFormId(candidate) ?? getFormId(contentObject) ?? getFormId(message as Record<string, any>),
    ...(typeof answerId === 'string' ? { answer_id: answerId } : {}),
    type: 'show_form',
    fields: candidate.fields
  }
}

export const getSubmittedFormFields = (
  message: FormHistoryRecord | Record<string, any>,
  contentObject: Record<string, any> | null = parseFormObject(message?.content)
): Record<string, unknown> | null => {
  const fields =
    contentObject?.submitted_fields ??
    contentObject?.submittedFields ??
    message?.submitted_fields ??
    message?.submittedFields ??
    (!Array.isArray(contentObject?.fields) ? contentObject?.fields : undefined) ??
    (!Array.isArray(message?.fields) ? message?.fields : undefined)

  return fields && typeof fields === 'object' && !Array.isArray(fields) && Object.keys(fields).length > 0
    ? (fields as Record<string, unknown>)
    : null
}

export const isSubmittedFormFieldsRedacted = (
  message: FormHistoryRecord | Record<string, any>,
  contentObject: Record<string, any> | null = parseFormObject(message?.content)
): boolean =>
  message?.submitted_fields_redacted === true ||
  contentObject?.submitted_fields_redacted === true ||
  message?.form_config?.submitted_fields_redacted === true ||
  contentObject?.form_config?.submitted_fields_redacted === true

export const getFormHistoryRecordKind = (
  message: FormHistoryRecord,
  formId: string | undefined
): 'issued' | 'answer' | undefined => {
  const content = typeof message.content === 'string' ? message.content : undefined
  const answerId = message.answer_id ?? message.answerId

  if (message.role === 'form' && formId && content === formId) return 'issued'
  if (
    message.role === 'form' &&
    content &&
    typeof answerId === 'string' &&
    content === answerId &&
    content !== formId
  ) {
    return 'answer'
  }

  const contentObject = parseFormObject(message.content)
  const explicitlySubmitted =
    message.type === 'form_submission' ||
    contentObject?.type === 'form_submission' ||
    contentObject?.submitted === true ||
    message.submitted === true ||
    !!contentObject?.submitted_fields ||
    !!message.submitted_fields

  if (explicitlySubmitted) return 'answer'
  if (message.type === 'show_form' || message.role === 'form') return 'issued'

  return undefined
}

export const collectFormConfigs = (messages: FormHistoryRecord[]): Map<string, FormContent> => {
  const configs = new Map<string, FormContent>()

  messages.forEach(message => {
    const config = getFormConfig(message)
    if (config?.form_id) configs.set(config.form_id, config)
  })

  return configs
}

export const normalizeFormHistoryMessage = (
  message: FormHistoryRecord,
  retainedFormConfigs: Map<string, FormContent>
): NormalizedFormHistoryMessage | null => {
  const contentObject = parseFormObject(message.content)
  const directConfig = getFormConfig(message, contentObject)
  const formId = getFormId(message as Record<string, any>) ?? directConfig?.form_id ?? getFormId(contentObject)
  const retainedConfig = formId ? retainedFormConfigs.get(formId) : undefined
  const formConfig = directConfig ?? retainedConfig
  const kind = getFormHistoryRecordKind(message, formId)

  if (!kind || !formId || !formConfig || !isFormFieldConfigList(formConfig.fields)) return null

  if (kind === 'issued') {
    return {
      role: 'assistant',
      type: 'show_form',
      content: {
        ...formConfig,
        form_id: formId,
        type: 'show_form',
        answered: false,
        submitted: false
      },
      _id: message._id,
      timestamp: message.timestamp,
      sessionId: message.session_id
    }
  }

  const submittedFields = getSubmittedFormFields(message, contentObject)
  const answerId = message.answer_id ?? message.answerId
  const submittedFieldsRedacted = isSubmittedFormFieldsRedacted(message, contentObject)
  if (!submittedFields && !submittedFieldsRedacted) return null

  return {
    role: 'user',
    type: 'show_form',
    content: {
      ...formConfig,
      form_id: formId,
      ...(typeof answerId === 'string' ? { answer_id: answerId } : {}),
      type: 'show_form',
      answered: true,
      submitted: true,
      submitted_fields: submittedFields ?? {},
      ...(submittedFieldsRedacted ? { submitted_fields_redacted: true } : {})
    },
    _id: message._id,
    timestamp: message.timestamp,
    sessionId: message.session_id
  }
}
